/**
 * Admin → Billing → Upgrade prompts: where the platform asks unpaid accounts
 * to upgrade. One card per placement: on/off, who sees it (billing states),
 * and its wording. Saved as a whole through billing/upgrade_prompts_set; the
 * platform picks changes up on its next load (ui-config is cached 60 s).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Msg, panelGet, panelPost } from '../kit';

type Audience = 'explorer' | 'trial_active' | 'trial_lapsed';
type Settings = Record<string, Record<string, any>>;

const AUDIENCE_LABEL: Record<Audience, string> = {
  explorer: 'Explorer (free)',
  trial_active: 'On a Voyager trial',
  trial_lapsed: 'Trial ended',
};
const FREQ_LABEL: Record<string, string> = {
  sign_in: 'Every sign-in',
  daily: 'Once a day (first visit)',
  weekly: 'Once a week',
};

interface PlacementDef {
  key: string;
  title: string;
  where: string;
  /** `optional`: shown only when the server stores that field (a newer key
   *  an older backend would reject on save). */
  fields: Array<{ key: string; label: string; multiline?: boolean; optional?: boolean }>;
  frequency?: boolean;
  /** Extra line under the fields, e.g. the tokens this placement's text takes. */
  help?: string;
}

const PLACEMENTS: PlacementDef[] = [
  {
    key: 'search_button', title: '“Upgrade to Voyager” on Cosmos search',
    where: 'On the web, at the top of the Search history column in place of Invite / Feedback / Bug (subscribers keep those); on a phone, at the right of the Filters / History row. Trial accounts see the days left.',
    fields: [{ key: 'label', label: 'Button label' }],
  },
  {
    key: 'signin_modal', title: 'Sign-in upgrade sheet',
    where: 'The plans sheet opens by itself right after a sign-in, with this headline and message. Never over onboarding or another sheet. Admin → Onboarding → Go live switches it off (the new onboarding opens no sheet on entry), and Roll back restores it.',
    fields: [{ key: 'headline', label: 'Headline' }, { key: 'message', label: 'Message', multiline: true }],
    frequency: true,
  },
  {
    key: 'nav_button', title: '“Upgrade” button in the side rail',
    where: 'Always visible above the profile picture (and as a pill in the mobile header). Trial accounts also see the days left.',
    fields: [{ key: 'label', label: 'Button label' }],
  },
  {
    key: 'banner', title: 'Banner on Cosmos search',
    where: 'A strip above the search box. Dismissible; it comes back in the next browser session.',
    fields: [
      { key: 'message_explorer', label: 'Message for Explorer', multiline: true },
      { key: 'message_trial_active', label: 'Message during a trial', multiline: true },
      { key: 'message_trial_lapsed', label: 'Message after a trial ended', multiline: true },
      { key: 'cta', label: 'Button label' },
    ],
  },
  {
    key: 'filter_card', title: 'Upgrade card in the filter panel',
    where: 'Replaces the small “Full filtering is a paid feature” line at the top of the locked filters.',
    fields: [{ key: 'headline', label: 'Headline' }, { key: 'cta', label: 'Button label' }],
  },
  {
    key: 'trial_card', title: 'Trial countdown card in the filter panel',
    where: 'At the top of the filters during a Voyager trial, with the time left (updated every minute). The button opens the plans sheet. Shown only to accounts on a trial.',
    fields: [
      { key: 'headline', label: 'Headline' },
      { key: 'message', label: 'Message', multiline: true },
      { key: 'cta', label: 'Button label' },
    ],
    help: 'Tokens: {time_left} (e.g. “3 days 4 hours”, “5 h 12 min”), {days_left}, {first_name}',
  },
  {
    key: 'picks_block', title: 'Upgrade block under the Cosmos picks',
    where: 'Onboarding v2 (Admin → Onboarding): below the 3 technologies an Explorer lands on. “Start free trial” shows only while the account can start a trial, with the note under it; “Upgrade to Voyager” opens the plans sheet. Off hides the block; the 3 cards stay.',
    fields: [
      { key: 'message', label: 'Message' },
      { key: 'sub', label: 'Line under it', multiline: true, optional: true },
      { key: 'trial_cta', label: 'Trial button label' },
      { key: 'trial_note', label: 'Note under the trial button' },
      { key: 'cta', label: 'Upgrade button label' },
    ],
    help: 'Token: {tech_count} (the live number of tech offers, e.g. 29,608)',
  },
];

export const UpgradePromptsPanel: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr('');
    const r = await panelGet('billing', 'upgrade_prompts');
    if (r.error) { setErr(r.error); return; }
    setData(r.data);
    setDraft(JSON.parse(JSON.stringify(r.data.settings)));
  };
  useEffect(() => { void load(); }, []);

  const dirty = useMemo(() => !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(data.settings),
    [data, draft]);

  const set = (place: string, key: string, value: any) => {
    setOk('');
    setDraft(d => d ? { ...d, [place]: { ...d[place], [key]: value } } : d);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true); setErr(''); setOk('');
    const r = await panelPost('billing', 'upgrade_prompts_set', { settings: draft });
    setBusy(false);
    if (r.error) { setErr(r.error); return; }
    setData(r.data);
    setDraft(JSON.parse(JSON.stringify(r.data.settings)));
    setOk('Saved. The platform picks it up on its next page load (within a minute).');
  };

  if (!draft || !data) {
    return err ? <Msg kind="error">{err}</Msg> : <div className="text-sm text-grayscale-1100">Loading…</div>;
  }
  const limits: Record<string, number> = data.limits || {};

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[13px] text-grayscale-1200 max-w-3xl">
        Where Cosmos asks unpaid accounts to upgrade. Paying and admin-assigned Voyager accounts never see
        any of these. Every button opens the plans sheet; its clicks are recorded as
        <code className="mx-1 text-[12px]">upgrade prompt: …</code> in the checkout funnel (the filter card as
        “full filtering”), so you can see which prompt a purchase came from. The automatic sign-in sheet
        does not count as hitting the paywall until they click in it. Text can use <code className="text-[12px]">{'{first_name}'}</code> and
        <code className="ml-1 text-[12px]">{'{days_left}'}</code> (the trial card also <code className="text-[12px]">{'{time_left}'}</code>);
        a blank field goes back to the default wording.
      </div>

      {/* Only placements this server knows: saving one it does not would
          fail the whole save (e.g. picks_block before the onboarding v2 backend). */}
      {PLACEMENTS.filter(pl => !!data.settings?.[pl.key]).map(pl => {
        const cur = draft[pl.key] || {};
        return (
          <div key={pl.key} className="bg-white rounded-xl border border-grayscale-300 p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="max-w-2xl">
                <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{pl.title}</div>
                <div className="text-xs text-grayscale-1100 mt-0.5">{pl.where}</div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={!!cur.enabled} onChange={e => set(pl.key, 'enabled', e.target.checked)} />
                {cur.enabled ? 'On' : 'Off'}
              </label>
            </div>

            <div className={`flex flex-col gap-3 ${cur.enabled ? '' : 'opacity-50'}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-xs text-grayscale-1100 w-20">Show to</span>
                {((data.place_audiences?.[pl.key] || data.audiences) as Audience[]).map(a => (
                  <label key={a} className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={(cur.audiences || []).includes(a)}
                      onChange={e => set(pl.key, 'audiences', e.target.checked
                        ? [...(cur.audiences || []), a] : (cur.audiences || []).filter((x: string) => x !== a))} />
                    {AUDIENCE_LABEL[a]}
                  </label>
                ))}
              </div>
              {pl.frequency && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-xs text-grayscale-1100 w-20">How often</span>
                  <select value={cur.frequency} onChange={e => set(pl.key, 'frequency', e.target.value)}
                    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
                    {(data.frequencies as string[]).map(f => <option key={f} value={f}>{FREQ_LABEL[f] || f}</option>)}
                  </select>
                </div>
              )}
              {pl.fields.filter(f => !f.optional || f.key in (data.settings?.[pl.key] || {})).map(f => {
                const v = String(cur[f.key] ?? '');
                const lim = limits[f.key];
                const over = lim && v.length > lim;
                return (
                  <label key={f.key} className="flex flex-col gap-1 text-xs text-grayscale-1100">
                    <span className="flex justify-between">
                      <span>{f.label}</span>
                      {lim ? <span className={over ? 'text-red-600' : ''}>{v.length}/{lim}</span> : null}
                    </span>
                    {f.multiline ? (
                      <textarea value={v} rows={2} onChange={e => set(pl.key, f.key, e.target.value)}
                        className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
                    ) : (
                      <input value={v} onChange={e => set(pl.key, f.key, e.target.value)}
                        className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
                    )}
                  </label>
                );
              })}
              {pl.help && <div className="text-[11px] text-grayscale-1100">{pl.help}</div>}
            </div>
          </div>
        );
      })}

      <div className="flex items-center gap-2 sticky bottom-0 bg-grayscale-100/90 py-2">
        <Btn kind="primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn onClick={() => { setOk(''); setDraft(JSON.parse(JSON.stringify(data.settings))); }} disabled={!dirty || busy}>
          Discard changes
        </Btn>
        {dirty && <span className="text-xs text-grayscale-1100">Unsaved changes</span>}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {ok && <Msg kind="ok">{ok}</Msg>}
    </div>
  );
};

export default UpgradePromptsPanel;
